/**
 * Cache wrapper for "shifts since cutoff". TtlCache keyed by the cutoff ISO so a calendar-day
 * rollover invalidates correctly. Fetches shifts whose Sched Date OR Shift Start Final is on or
 * after the cutoff: Sched Date is the scheduling axis (a shift with a Sched Date but no Start
 * Final must not be missed — the scheduling list keys purely on Sched Date), while the Start
 * Final arm keeps the other consumer (the Robots page, which windows on start/end times) whole.
 */

import { Shift } from "@domain/CRM/shift";
import { TtlCache } from "../../../cache/ttlCache";
import { API } from "../../../api";

const SINCE_CUTOFF_TTL_MS = 15 * 60 * 1000;

/** The one query behind both the TTL fetcher and `refresh()`; `fresh` also bypasses the
 *  server-side Airtable cache (`?fresh=1`). */
const fetchShiftsSince = (cutoffIso: string, fresh?: boolean): Promise<Shift[]> =>
	API.Data.Shifts.get.byFilter(
		{
			type: "OR",
			fields: [
				{ scheduledDate: { operator: ">=", value: cutoffIso } },
				{ startDate: { operator: ">=", value: cutoffIso } },
			],
		},
		fresh,
	);

class CachedShiftsSinceCutoff {
	private cache = new TtlCache<string, Shift[]>(SINCE_CUTOFF_TTL_MS, (cutoffIso) => fetchShiftsSince(cutoffIso));

	/** Synchronous TTL cache hit for the given cutoff, or null if missing/expired. */
	public peek(cutoff: Date): Shift[] | null {
		return this.cache.peek(cutoff.toISOString());
	}

	public get(cutoff: Date): Promise<Shift[]> {
		return this.cache.get(cutoff.toISOString());
	}

	/**
	 * Bypass BOTH caches — the server's (`?fresh=1`) and this TTL entry — and overwrite the entry
	 * with the live result. The Scheduling board's refresh-in-place gesture: shifts edited outside
	 * the portal (Airtable, Mission Control) never invalidate either cache, so without this they
	 * stay stale for up to 15 min. A failed fetch leaves the previous entry in place.
	 */
	public async refresh(cutoff: Date): Promise<Shift[]> {
		const key = cutoff.toISOString();
		const shifts = await fetchShiftsSince(key, true);
		this.cache.set(key, shifts);
		return shifts;
	}

	public clear(): void {
		this.cache.clear();
	}
}

export class CachedShiftsAPI {
	public sinceCutoff = new CachedShiftsSinceCutoff();
}
